import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MAX_NAME_CHARS, type InviteLink, type InviteRow } from '@2x3/shared';
import { api, ApiError } from '../lib/api.js';
import { plural, timeAgo, utcStamp } from '../lib/format.js';
import { useSession } from '../lib/session.js';

/**
 * Invites, for WB5SKM. The page is shown to an invite that carries the admin
 * flag, which only SSH can set. A new link is shown ONCE, here, and never again:
 * the server keeps only its hash.
 */

function invitationText(invite: InviteLink, name: string | null, from: string): string {
  return [
    `Hi ${name || invite.callsign},`,
    '',
    "I'm building 2x3skimmer, a CW skimmer that copies every signal in a band at once. To test it honestly I need " +
      "real on-air CW copied by ear, and I'd be glad of your help.",
    '',
    `Your personal link - it signs you in as ${invite.callsign}, so please keep it to yourself:`,
    invite.link,
    '',
    'Listen, copy what you hear, and save. The "How to copy" page on the site takes two minutes.',
    '',
    '73,',
    from,
  ].join('\n');
}

/**
 * Copy to the clipboard. The async Clipboard API first; where a browser (or an
 * embedded one) refuses it, select the field and try the older copy command;
 * failing both, the text is left selected for Ctrl+C.
 */
async function copyText(text: string, field: HTMLInputElement | HTMLTextAreaElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    if (!field) return false;
    field.focus();
    field.select();
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    }
  }
}

function NewLink({
  invite,
  name,
  from,
  reissued,
  onDone,
}: {
  invite: InviteLink;
  name: string | null;
  from: string;
  reissued: boolean;
  onDone: () => void;
}) {
  const [message, setMessage] = useState(() => invitationText(invite, name, from));
  const [copied, setCopied] = useState<'link' | 'message' | null>(null);
  const [manual, setManual] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);

  const copy = async (what: 'link' | 'message'): Promise<void> => {
    const ok = await copyText(what === 'link' ? invite.link : message, what === 'link' ? linkRef.current : messageRef.current);
    setCopied(ok ? what : null);
    setManual(!ok);
  };

  const mailto =
    `mailto:?subject=${encodeURIComponent('Copy some CW for 2x3skimmer?')}` + `&body=${encodeURIComponent(message)}`;

  return (
    <section className="new-link" aria-live="polite">
      <h2>
        {reissued ? 'New link for' : 'Invite ready for'} <span className="call">{invite.callsign}</span>
      </h2>
      <p className="alert">
        <strong>Copy it now - this link is shown once.</strong> Only its hash is kept, so it can be replaced but never
        shown again.{reissued ? ' The old link has stopped working.' : ''} Whoever opens it copies as{' '}
        {invite.callsign}: send it to them and nobody else.
      </p>

      <label className="field">
        <span>Link</span>
        <div className="copy-row">
          <input ref={linkRef} type="text" readOnly value={invite.link} onFocus={(e) => e.currentTarget.select()} />
          <button type="button" className="btn-primary" onClick={() => void copy('link')}>
            {copied === 'link' ? 'Copied' : 'Copy link'}
          </button>
        </div>
      </label>

      <label className="field">
        <span>
          Message <span className="muted">(edit as you like)</span>
        </span>
        <textarea ref={messageRef} value={message} rows={12} onChange={(e) => setMessage(e.target.value)} />
      </label>
      {manual && <p className="muted small">This browser would not copy for us: the text is selected - press Ctrl+C.</p>}

      <div className="form-actions">
        <button type="button" onClick={() => void copy('message')}>
          {copied === 'message' ? 'Message copied' : 'Copy message'}
        </button>
        <a className="btn-link" href={mailto}>
          Open in email
        </a>
        <button type="button" className="btn-quiet" onClick={onDone}>
          Done
        </button>
      </div>
    </section>
  );
}

function Status({ row }: { row: InviteRow }) {
  if (row.revokedAt) return <span className="badge is-off">revoked</span>;
  if (!row.lastSeenAt) return <span className="badge">not opened yet</span>;
  return <span className="badge is-good">active</span>;
}

export default function Admin() {
  const { me, loading } = useSession();
  const [rows, setRows] = useState<InviteRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [callsign, setCallsign] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [issued, setIssued] = useState<{ invite: InviteLink; name: string | null; reissued: boolean } | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.admin.invites());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (me?.admin) void load();
  }, [me?.admin, load]);

  const act = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const create = (e: FormEvent): void => {
    e.preventDefault();
    void act(async () => {
      const invite = await api.admin.add(callsign, name, note);
      setIssued({ invite, name: name.trim() || null, reissued: false });
      setCallsign('');
      setName('');
      setNote('');
    });
  };

  const reissue = (row: InviteRow): void => {
    const warn = row.revokedAt
      ? `Issue ${row.callsign} a new link? It restores their access.`
      : `Issue ${row.callsign} a new link? Their current link stops working at once.`;
    if (!window.confirm(warn)) return;
    void act(async () => {
      const invite = await api.admin.reissue(row.callsign);
      setIssued({ invite, name: row.name, reissued: true });
    });
  };

  const revoke = (row: InviteRow): void => {
    if (!window.confirm(`Revoke ${row.callsign}'s link? Their copies are kept; "New link" restores access.`)) return;
    void act(async () => {
      await api.admin.revoke(row.callsign);
    });
  };

  const remove = (row: InviteRow): void => {
    const warn =
      row.copies > 0
        ? `Remove ${row.callsign} AND DELETE their ${plural(row.copies, 'copy', 'copies')}? This cannot be undone. ` +
          'Revoke instead keeps the copies.'
        : `Remove ${row.callsign}'s invite?`;
    if (!window.confirm(warn)) return;
    void act(async () => {
      await api.admin.remove(row.callsign, row.copies > 0);
    });
  };

  if (loading) return <p className="muted">Loading...</p>;
  if (!me?.admin) {
    return (
      <div className="join">
        <h1>Admin</h1>
        <p>This page is for WB5SKM. Open your admin invite link on this device to use it.</p>
        <p>
          <Link to="/">Home</Link>
        </p>
      </div>
    );
  }

  const active = rows?.filter((r) => !r.revokedAt).length ?? 0;

  return (
    <div className="admin">
      <div className="page-head">
        <h1>Invites</h1>
        <p className="muted">
          Only you see this page. {rows ? `${plural(active, 'active invite')}, ${plural(rows.reduce((n, r) => n + r.copies, 0), 'copy', 'copies')} so far.` : ''}
        </p>
      </div>

      {error && (
        <p className="alert is-error" role="alert">
          {error}
        </p>
      )}

      {issued ? (
        <NewLink
          invite={issued.invite}
          name={issued.name}
          from={me.callsign}
          reissued={issued.reissued}
          onDone={() => setIssued(null)}
        />
      ) : (
        <form className="invite-form" onSubmit={create}>
          <h2>Invite an operator</h2>
          <div className="invite-fields">
            <label className="field">
              <span>Callsign</span>
              <input
                type="text"
                value={callsign}
                onChange={(e) => setCallsign(e.target.value.toUpperCase())}
                placeholder="W1ABC"
                maxLength={20}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                required
              />
            </label>
            <label className="field">
              <span>
                Name <span className="muted">(optional)</span>
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Bob"
                maxLength={MAX_NAME_CHARS}
              />
            </label>
          </div>
          <label className="field">
            <span>
              Private note <span className="muted">(optional - only you see it)</span>
            </span>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="CWops member, met at the club meeting"
              maxLength={500}
            />
          </label>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={busy || callsign.trim() === ''}>
              {busy ? 'Creating...' : 'Create invite'}
            </button>
            <span className="muted small">You get the link and a message to send, once.</span>
          </div>
        </form>
      )}

      <h2>Operators</h2>
      {!rows && !error && <p className="muted">Loading...</p>}
      {rows && rows.length === 0 && <p className="muted">Nobody invited yet.</p>}
      {rows && rows.length > 0 && (
        <table className="clip-table invite-table">
          <thead>
            <tr>
              <th>Callsign</th>
              <th>Name</th>
              <th className="num">Copies</th>
              <th>Last seen</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.callsign} className={r.revokedAt ? 'is-mine' : ''}>
                <td>
                  <span className="call">{r.callsign}</span>
                  {r.admin && <span className="badge">admin</span>}
                  {r.note && <div className="muted small wrap">{r.note}</div>}
                </td>
                <td>{r.name ?? <span className="muted">-</span>}</td>
                <td className="num">{r.copies}</td>
                <td title={r.lastSeenAt ? utcStamp(r.lastSeenAt) : undefined}>{timeAgo(r.lastSeenAt)}</td>
                <td>
                  <Status row={r} />
                </td>
                <td>
                  {r.admin ? (
                    <span className="muted small">changed over SSH only</span>
                  ) : (
                    <div className="row-actions">
                      <button type="button" disabled={busy} onClick={() => reissue(r)}>
                        New link
                      </button>
                      {!r.revokedAt && (
                        <button type="button" disabled={busy} onClick={() => revoke(r)}>
                          Revoke
                        </button>
                      )}
                      <button type="button" className="btn-danger" disabled={busy} onClick={() => remove(r)}>
                        Remove
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">
        <strong>New link</strong> replaces a lost or leaked link (the old one stops working), and restores a revoked
        one. <strong>Revoke</strong> stops a link but keeps every copy made with it. <strong>Remove</strong> deletes the
        invite, and its copies with it.
      </p>
    </div>
  );
}
